import * as IoC from 'typescript-ioc';
import { action, observable, runInAction } from 'mobx';
import { ConnectionType, IConfigurePassportDisplayDTO, IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../../api/connections.dao';
import { TConnection } from '../../../../../../api/blueprints/connection.template.types';
import { defaultPassportPreviewValues, IConfigurePassportDisplayFormProps } from '../../configure-passport-display/configure-passport-display.viewmodel';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { configList, PassportConfigurationMode } from '../../configure-passport-display/configure-passport-display.config';
import { IAudioProviderInfoDTO, IPhoneNumbersSet, IZoomDAO } from '../../../../../../api/zoom.dao';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { PASSPORT_PREVIEW_CONNECTION_GROUP_TEXT } from '../../../../../../constants/common-labels.constants';
import { IntlShape } from 'react-intl';

export interface IPassportPreviewContextParams {
    meetingId?: number;
    roomId?: number;
}
export interface IConnectionGroupsPreviewProps extends IConfigurePassportDisplayDTO {
    connection: IConnectionDetailsDTO;
    phoneNumberSet?: IPhoneNumbersSet[];
}

class ConnectionGroupsPassportPreviewViewModel {
    @observable
    private isLoading: boolean = false;
    @observable
    private popoverTitle: string = '';
    @observable
    private isPopoverOpen: boolean = false;
    @observable
    private connectionGroups: IConnectionDetailsDTO[] | TConnection[];
    @observable
    private previewGroup: IConnectionGroupsPreviewProps[] = [];
    private passportPreviewContextParams: IPassportPreviewContextParams;
    private intl: IntlShape;
    public passportConfigurationMode: PassportConfigurationMode;
    constructor(
        passportPreviewContextParams: IPassportPreviewContextParams,
        groups: IConnectionDetailsDTO[] | TConnection[],
        popoverTitle: string,
        passportConfigurationMode: PassportConfigurationMode,
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
    ) {
        this.passportPreviewContextParams = passportPreviewContextParams;
        this.connectionGroups = groups;
        this.intl = this.intlProvider.getIntl();
        this.popoverTitle = popoverTitle
            ? `${this.intl.formatMessage(PASSPORT_PREVIEW_CONNECTION_GROUP_TEXT)}: ${popoverTitle}`
            : this.intl.formatMessage(PASSPORT_PREVIEW_CONNECTION_GROUP_TEXT);
        this.passportConfigurationMode = passportConfigurationMode;
    }

    public getIsPopoverOpen = (): boolean => {
        return this.isPopoverOpen;
    };

    public getPopoverTitle = (): string => {
        return this.popoverTitle;
    };

    public getIsLoading = (): boolean => {
        return this.isLoading;
    };

    public getConnectionGroups = (): IConnectionDetailsDTO[] | TConnection[] => {
        return this.connectionGroups;
    };

    public getConnectionGroupsForPreview = (): IConnectionGroupsPreviewProps[] => {
        return this.previewGroup;
    };

    public getIsPreviewDisabled = (): boolean => {
        return !this.connectionGroups.length;
    };

    @action
    public closePopover = (): void => {
        this.isPopoverOpen = false;
    };

    @action
    public openPopover = (): void => {
        this.isPopoverOpen = true;
        this.setPreviewGroup();
    };

    private setPreviewGroup = async (): Promise<void> => {
        if (this.passportConfigurationMode === PassportConfigurationMode.MeetingConnection) {
            this.fetchConnectionGroupsPassportConfigureDisplay();
        } else if (this.passportConfigurationMode === PassportConfigurationMode.ConnectionTemplate) {
            const previewGroupList = await Promise.all(
                this.connectionGroups.map(async item => {
                    const value = await this.getConnectionTemplatePassportPreviewValues(item);
                    return value;
                })
            );
            runInAction(() => {
                this.previewGroup = previewGroupList;
            });
        }
    };

    public getAbstractFormViewModelController = (connection: IConnectionDetailsDTO | TConnection): AbstractFormViewModelController => {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(connection, 'ConnectionTemplatesFormVM', configList);
            }
        }();
    };

    @action
    private fetchConnectionPassportConfigureDisplay = async (connection: IConnectionDetailsDTO & TConnection): Promise<IConfigurePassportDisplayDTO> => {
        const props = this.formConfigurePassportDisplayFormProps(connection);
        return this.passportPreviewContextParams?.roomId
            ? await this.connectionsDAO.getRoomConnectionPassportConfigureDisplay(props)
            : await this.connectionsDAO.getConnectionPassportConfigureDisplay(props);
    };

    private fetchPhoneNumberSet = (connection: IConnectionDetailsDTO & TConnection): Promise<IAudioProviderInfoDTO | null> => {
        if (connection.audioProviderId) {
            return this.zoomDAO.getPhoneNumbersSet({ audioProviderId: connection.audioProviderId });
        } else {
            return null;
        }
    };

    private getTelephoneNumbers = async (connection: IConnectionDetailsDTO & TConnection): Promise<IPhoneNumbersSet[]> => {
        try {
            const phoneNumberSetResponse = await this.fetchPhoneNumberSet(connection);
            return phoneNumberSetResponse?.telephoneNumbers || [];
        } catch (e) {
            this.alertError(e);
        }

    };

    private fetchConnectionGroupsPreviewData = async (connection: IConnectionDetailsDTO & TConnection): Promise<IConnectionGroupsPreviewProps> => {
        const connectionGroupsPreviewProps = {
            connection: connection,
            phoneNumberSet: [],
        };
        try {
            if (connection.type === ConnectionType.AudioBridge) {
                const phoneNumberSet = await this.getTelephoneNumbers(connection);
                connectionGroupsPreviewProps.phoneNumberSet = phoneNumberSet;
            }
            const passportConfigureDisplayResponse = await this.fetchConnectionPassportConfigureDisplay(connection);
            return {
                ...connectionGroupsPreviewProps,
                ...passportConfigureDisplayResponse,
            };
        } catch (e) {
            return {
                ...connectionGroupsPreviewProps,
                ...defaultPassportPreviewValues,
            };
        }
    };

    private getConnectionTemplatePassportPreviewValues = async (connection: IConnectionDetailsDTO & TConnection): Promise<IConnectionGroupsPreviewProps> => {
        const connectionGroupsPreviewProps = {
            connection: connection,
            phoneNumberSet: [],
        };
        if (connection.type === ConnectionType.AudioBridge) {
            const phoneNumberSet = await this.getTelephoneNumbers(connection);
            connectionGroupsPreviewProps.phoneNumberSet = phoneNumberSet;
        }

        if (connection?.passportConfiguration) {
            return {
                ...connectionGroupsPreviewProps,
                ...connection.passportConfiguration
            };
        } else {
            return {
                ...connectionGroupsPreviewProps,
                ...defaultPassportPreviewValues,
            };
        }

    };

    private fetchConnectionGroupsPassportConfigureDisplay = async (): Promise<void> => {
        this.isLoading = true;
        Promise.all(this.connectionGroups.map(item => {
            return this.fetchConnectionGroupsPreviewData(item);
        }))
            .then(res => {
                runInAction(() => {
                    this.previewGroup = res;
                });
            })
            .catch(this.alertError)
            .finally(() => runInAction(() => this.isLoading = false));
    };

    private formConfigurePassportDisplayFormProps = (connection: IConnectionDetailsDTO & TConnection): IConfigurePassportDisplayFormProps => {
        return {
            ...this.passportPreviewContextParams,
            eventId: this.globalStorage.getEvent().eventId,
            accountId: this.globalStorage.getEvent().accountId,
            meetingConnectionId: connection?.meetingConnectionId,
        };
    };

    private alertError = (e: string): void => {
        const message = this.errorParsingService.extractMessage(e);
        this.alertsService.alert({ message: message });
    };
}

export { ConnectionGroupsPassportPreviewViewModel };
